import { lazy, ComponentType } from "react";
import { checkVersionMismatch } from "@/hooks/useAppVersion";

const isDev = import.meta.env.DEV;

// Shorter delays in dev mode since Vite reconnects quickly
const DEV_DELAY = 500;
const PROD_DELAY = 1500;

/**
 * Wrapper around React.lazy that adds retry logic for failed chunk loads.
 * This helps recover from network issues or deployment-related chunk failures.
 * Now version-aware: checks if app has been updated before forcing reload.
 * Dev-mode aware: uses shorter delays and listens for Vite reconnection.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = isDev ? 5 : 3, // More retries in dev mode
  delay = isDev ? DEV_DELAY : PROD_DELAY
): React.LazyExoticComponent<T> {
  return lazy(() => retryImport(importFn, retries, delay));
}

async function retryImport<T>(
  importFn: () => Promise<T>,
  retries: number,
  delay: number
): Promise<T> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      // Wait before retry attempts (with exponential backoff, capped in dev)
      if (attempt > 0) {
        const waitTime = isDev 
          ? Math.min(delay * attempt, 2000) // Cap at 2s in dev
          : delay * attempt;
        console.log(`[LazyRetry] Attempt ${attempt + 1}/${retries + 1}, waiting ${waitTime}ms...`);
        
        // In dev mode, also wait for potential Vite reconnection
        if (isDev) {
          await Promise.race([
            new Promise(resolve => setTimeout(resolve, waitTime)),
            waitForViteReconnect(waitTime)
          ]);
        } else {
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }
      
      const result = await importFn();
      
      // If we succeeded after retries, log it
      if (attempt > 0) {
        console.log(`[LazyRetry] Module loaded successfully on attempt ${attempt + 1}`);
      }
      
      return result;
    } catch (error) {
      const isLastAttempt = attempt === retries;
      const isChunkError = isChunkLoadError(error);

      console.warn(
        `[LazyRetry] Module load failed (attempt ${attempt + 1}/${retries + 1}):`,
        error instanceof Error ? error.message : error
      );

      if (isLastAttempt) {
        // In dev mode, don't reload - let the error boundary handle it
        if (isDev) {
          console.warn('[LazyRetry] Dev mode: not reloading, showing error UI');
          throw error;
        }

        // On final failure in production, check if there's a version mismatch
        if (isChunkError) {
          const hasNewVersion = await checkVersionMismatch();
          
          if (hasNewVersion) {
            // Dispatch event to show update prompt instead of auto-reloading
            window.dispatchEvent(new CustomEvent('app-update-available'));
            
            // If user hasn't already tried reloading, do one reload
            if (!sessionStorage.getItem('chunk_reload_attempted')) {
              sessionStorage.setItem('chunk_reload_attempted', 'true');
              console.log('[LazyRetry] New app version detected, reloading...');
              
              // Clear service worker cache before reload
              if ('caches' in window) {
                caches.keys().then(names => {
                  names.forEach(name => caches.delete(name));
                });
              }
              
              window.location.reload();
              return new Promise(() => {}); // Never resolves, page is reloading
            }
          } else if (!sessionStorage.getItem('chunk_reload_attempted')) {
            // No version mismatch but chunk failed - might be network issue
            sessionStorage.setItem('chunk_reload_attempted', 'true');
            console.log('[LazyRetry] Attempting page reload to clear stale chunks...');
            window.location.reload();
            return new Promise(() => {});
          }
        }
        
        // Clear the flag for future navigations
        sessionStorage.removeItem('chunk_reload_attempted');
        throw error;
      }

      if (!isChunkError) {
        // Non-chunk errors shouldn't be retried
        throw error;
      }
    }
  }

  throw new Error("Failed to load module after retries");
}

/**
 * Wait for Vite to reconnect (dev mode only)
 */
function waitForViteReconnect(maxWait: number): Promise<void> {
  return new Promise((resolve) => {
    const handler = () => {
      window.removeEventListener('vite:reconnected', handler);
      console.log('[LazyRetry] Vite reconnected, retrying import...');
      resolve();
    };
    
    window.addEventListener('vite:reconnected', handler);
    
    // Also resolve after maxWait
    setTimeout(() => {
      window.removeEventListener('vite:reconnected', handler);
      resolve();
    }, maxWait);
  });
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  
  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();
  
  return (
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("loading chunk") ||
    message.includes("loading css chunk") ||
    message.includes("unable to preload") ||
    message.includes("failed to load module") ||
    message.includes("network error") ||
    name.includes("chunkloaderror") ||
    name === "typeerror" && message.includes("fetch")
  );
}
